import {
    ActionBar,
    ActionBarSeparator,
    Add,
    AlertModal,
    ArrowDown,
    Box,
    Button,
    CellBackgroudColor,
    Checkbox,
    Close,
    ContextualBanner,
    Delete,
    DeleteModal,
    Divider,
    Export,
    HighlightCell,
    IconButton,
    Info,
    LinkText,
    Modal,
    Pagination,
    QuickFilterButton,
    Remove,
    SearchBar,
    showToast,
    Status,
    StatusBadge,
    Table,
    TextBadge,
    TextCell,
    Tooltip,
    TooltipContent,
    TooltipTrigger,
    useSubPage,
    View,
} from "@bcc-ui/ui-components";
import classNames from "classnames";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { CellProps, Column } from "react-table";

import { MoveAssetsToGroupModal } from "./custom-views/MoveAssetsToGroupModal";
import style from "components/asset-management-system/asset-table.scss";
import { AssetColumnSelector } from "components/asset-management-system/AssetColumnSelector";
import AssetKebabMenu from "components/asset-management-system/AssetKebabMenu";
import { deduceRiskLevelValue, getRiskLevelStatus } from "components/asset-management-system/assetRiskUtils";
import AssetSidePanel from "components/asset-management-system/AssetSidePanel";
import { CreateAssetCustomViewSubpage } from "components/asset-management-system/custom-views/CreateAssetCustomViewSubpage";
import { formatAssetValue } from "components/asset-management-system/custom-views/tabs/value/steps/value-recovery-step/estimated-value-card/estimatedValue";
import { GradeBadge } from "components/asset-management-system/custom-views/tabs/value/steps/value-recovery-step/grade-badge/GradeBadge";
import { ActiveFilter, FilterDropdown, toAssetFilter } from "components/asset-management-system/FilterDropdown";
import { ManageAssetView } from "components/asset-management-system/manage-assets/ManageAssetView";
import CustomDatePickerView from "components/common-date-picker/CustomDatePicker";
import DataBearingIcon from "components/icons/DataBearing";
import { subpageContext } from "components/layout/ApplicationLayout";
import { useIsMobileViewport } from "components/navigation/hooks/useIsMobileViewport";
import BmpStatusBadge, { Status as BmpStatus } from "components/status-badge/StatusBadge";
import { TableFooter } from "components/table/TableFooter";
import TextWithTooltip from "components/table/TextWithTooltip";
import {
    DISPLAY_TO_FILTER_OPERATOR,
    FilterOperator,
    FilterPathType,
} from "domain/asset-management/assetFilterConstants";
import { AssetPathKey } from "domain/asset-management/assetPathMapping";
import {
    AssetCustomViewFilterEntry,
    formatDurationSeconds,
    getGradingActionTranslationKey,
    isLimitApproaching,
    toViewFilterSelection,
} from "domain/asset-management/assets";
import {
    AUTH_ASSET_CREATE,
    AUTH_ASSET_DELETE,
    AUTH_ASSET_VIEW,
    AUTH_CUSTOM_ASSET_VIEW_EDIT,
    AUTH_CUSTOM_ASSET_VIEW_VIEW,
} from "domain/authority";
import { ConditionalRuleData, getConditionalCellColorKey } from "domain/conditionalFormattingRules";
import { isGrade } from "domain/grade";
import {
    Asset,
    AssetColumn,
    AssetFieldValue,
    AssetFilter,
    AssetFilters,
    AssetSearchRequest,
    assetService,
    DataBearingFilter,
    DEFAULT_COLUMN_KEYS,
    mapColumnsToApiPaths,
    TrackingSummary,
} from "services/asset-management/AssetService";
import { createTranslatorPath } from "services/pathTranslator";
import { Category } from "services/statistics/UsageStatisticsService";
import { userSessionService } from "services/user/UserSessionService";
import { StoreState } from "store";
import { setTrackingSummary } from "store/trackingSummary";
import { resolveColor } from "utils/commonFunctions";
import { formatTimestamp } from "utils/format";
import { logger } from "utils/logging";
import { getObject, RepositoryKey, setObject } from "utils/repository";
import { useTheme } from "utils/useTheme";

import testIds from "testIds.json";

const PAGE_SIZE = 100;
const MAX_PAGE_NUMBERS_TO_SHOW = 3;
const BULK_DELETE_LIMIT = 300;
const REMOVE_FROM_GROUP_LIMIT = 10_000;
const DELETE_CONFIRMATION_WORD = "DELETE";
const RESTRICTED_COLUMNS = [
    "checked",
    "disabled",
    "assetDetails",
    "parentUuid",
    "hasChildren",
    "detailsTruncated",
    "groupUuids",
    "hasErasureReport",
    "hasSuccessfulErasure",
    "lockState",
    "isTracked",
    "isDataBearing",
    "hasHiddenChildren",
];

const formatDynamicColumnLabel = (key: string): string =>
    key.replace(/_/g, " ").replace(/\b\w/g, (character) => character.toUpperCase());

const buildRequestedPaths = (visibleColumnKeys: string[]): string[] => [
    ...new Set([...mapColumnsToApiPaths([...visibleColumnKeys, "uuid", "assetId"]), "parent_uuid", "has_children"]),
];

function computeColumns(columns: string[]): { availableColumns: AssetColumn[]; hiddenColumns: string[] } {
    const assetColumns = assetService.getAvailableColumns();
    const unsortedVisibleColumns = columns.map((key) => {
        const matchingColumn = assetColumns.find(
            (fetchedColumn) => fetchedColumn.key === key || fetchedColumn.apiPath === key
        );
        return matchingColumn
            ? { ...matchingColumn, visible: true }
            : { key, label: formatDynamicColumnLabel(key), visible: true, apiPath: key };
    });

    const visibleColumns = [
        ...unsortedVisibleColumns.filter((column) => column.apiPath === AssetPathKey.ASSET_ID),
        ...unsortedVisibleColumns.filter((column) => column.apiPath !== AssetPathKey.ASSET_ID),
    ];

    const visibleColumnKeys = new Set(visibleColumns.map((column) => column.key));
    const nonVisibleColumns = assetColumns
        .filter((column) => !visibleColumnKeys.has(column.key))
        .map((column) => ({ ...column, visible: false }));
    return {
        availableColumns: [...visibleColumns, ...nonVisibleColumns],
        hiddenColumns: nonVisibleColumns.map((column) => column.key),
    };
}

interface SelectedAsset {
    uuid: string;
    groupUuids?: string[];
}

interface TableState {
    data: Asset[];
    cursor?: string;
    scrollPosition?: number;
    searchQuery?: string;
    totalAssetCount: number;
    assetCount: number;
    selectedRiskLevel?: string;
    selectedRegion?: string;
    sortColumn: string;
    sortAscending: boolean;
    currentPage: number;
    totalPages: number;
}

interface SearchRequestOptions {
    cursor?: string;
    columnKeys: string[];
    sortAscending: boolean;
    sortColumn: string;
    searchQuery?: string;
    selectedRiskLevel?: string;
    selectedRegion?: string;
    dataBearingFilterForRequest?: DataBearingFilter;
}

interface Props {
    columns: string[];
    viewName?: string;
    viewId?: string;
    filters?: AssetFilters;
    startDate?: string;
    endDate?: string;
    viewFilters?: unknown;
    onAssetsChange?: (assets: Asset[]) => void;
    onTotalCountChange?: (count?: number) => void;
    onFiltersChange?: (filters: AssetFilter[]) => void;
    onSearchQueryChange?: (searchQuery?: string) => void;
    refreshKey?: number;
    onExportSelected?: (selectedIds: string[], visibleColumnKeys: string[]) => void;
    onAssetEdit?: (asset: Asset) => void;
    onAssetCreate?: () => void;
    onVisibleColumnsChange?: (columnKeys: string[]) => void;
    toShowRemoveFromGroup?: boolean;
    onRemoveFromGroup?: (selectedIds: string[]) => void;
    testId?: string;
}

const renderWithHighlight = (colorKey: string | undefined, content: React.ReactNode): React.ReactNode => {
    return colorKey ? (
        <HighlightCell backgroudColor={colorKey as CellBackgroudColor}>
            <TextCell>{content}</TextCell>
        </HighlightCell>
    ) : (
        <TextCell>{content}</TextCell>
    );
};

const ExpandArrow = ({ isExpanded }: { isExpanded: boolean }) => {
    const theme = useTheme();
    return (
        <ArrowDown
            color={theme.iconFillColor}
            size={16}
            className={classNames(style.expandArrow, {
                [style.expandArrowCollapsed]: !isExpanded,
            })}
        />
    );
};

const fetchChildren = async (
    parentUuid: string,
    columnKeys: string[],
    options: {
        searchQuery?: string;
        sortColumnApiPath?: string;
        sortAscending: boolean;
        filters: AssetFilter[];
        filterLogic?: string;
    }
): Promise<Asset[]> => {
    const response = await assetService.searchAssets({
        parentAssetUuid: parentUuid,
        paths: buildRequestedPaths(columnKeys),
        size: PAGE_SIZE,
        sortAscending: options.sortAscending,
        sortColumn: options.sortColumnApiPath,
        sortColumnType: FilterPathType.STRING,
    });
    return response.assets;
};

const AssetTable = (props: Props): React.JSX.Element => {
    const { t } = useTranslation();
    const { current: abortControllers } = React.useRef<AbortController[]>([]);

    const translatedPaths = createTranslatorPath(useTranslation, "assetPaths");
    const globalTrackingSummary = useSelector((state: StoreState) => state.trackingSummaryReducer.trackingSummary);
    const assetViews = useSelector((state: StoreState) => state.assetViewsReducer.views);
    const dispatch = useDispatch();
    const [isSidePanelOpen, setIsSidePanelOpen] = useState(false);
    const [selectedAsset, setSelectedAsset] = useState<Asset | undefined>(undefined);
    const [tableState, setTableState] = useState<TableState>(() => {
        const saved = getObject<{ sortBy?: Array<{ id: string; desc: boolean }> }>(RepositoryKey.ALL_ASSETS_VIEW_TABLE);
        const initialSort = saved?.sortBy?.[0];
        return {
            data: [],
            totalAssetCount: 0,
            assetCount: 0,
            sortColumn: initialSort?.id ?? "",
            sortAscending: initialSort !== undefined ? !initialSort.desc : true,
            currentPage: 1,
            totalPages: 1,
            selectedRiskLevel: props.filters?.riskLevel?.[0],
        };
    });
    const [isAssetDetailsLoading, setAssetDetailsLoading] = useState(true);
    const assetDetailsAbortControllerRef = useRef<AbortController>();
    const [loading, setLoading] = useState(true);
    const [initialLoading, setInitialLoading] = useState(true);
    const [availableColumns, setAvailableColumns] = useState<AssetColumn[]>(
        () => computeColumns(props.columns).availableColumns
    );
    const [hiddenColumns, setHiddenColumns] = useState<string[]>(() => computeColumns(props.columns).hiddenColumns);
    const [tableTrackingSummary, setTableTrackingSummary] = useState<TrackingSummary | undefined>(undefined);
    const [dataBearingFilter, setDataBearingFilter] = useState<DataBearingFilter>(DataBearingFilter.ALL);
    const [selectedDateRange, setSelectedDateRange] = useState<{ startDate?: string; endDate?: string }>({
        startDate: props.startDate,
        endDate: props.endDate,
    });
    const [requestFailureMessage, setRequestFailureMessage] = useState<string | undefined>(undefined);
    const viewFilterKey = props.viewId ?? "default";
    const getViewFilters = (): ActiveFilter[] =>
        (getObject<Record<string, ActiveFilter[]>>(RepositoryKey.ALL_ASSETS_VIEW_FILTERS) ?? {})[viewFilterKey] ?? [];
    const saveViewFilters = (filters: ActiveFilter[]) => {
        const all = getObject<Record<string, ActiveFilter[]>>(RepositoryKey.ALL_ASSETS_VIEW_FILTERS) ?? {};
        setObject(RepositoryKey.ALL_ASSETS_VIEW_FILTERS, { ...all, [viewFilterKey]: filters });
    };
    const removeViewFilters = () => {
        const all = getObject<Record<string, ActiveFilter[]>>(RepositoryKey.ALL_ASSETS_VIEW_FILTERS) ?? {};
        const rest = Object.fromEntries(Object.entries(all).filter(([key]) => key !== viewFilterKey));
        setObject(RepositoryKey.ALL_ASSETS_VIEW_FILTERS, rest);
    };
    const [activeFilters, setActiveFilters] = useState<ActiveFilter[]>(() => getViewFilters());
    const [searchInput, setSearchInput] = useState<string | undefined>(undefined);
    const conditionalRules = useMemo<ConditionalRuleData[]>(
        () => getObject<ConditionalRuleData[]>(RepositoryKey.CONDITIONAL_RULES) ?? [],
        []
    );
    const [selectedAssetIds, setSelectedAssetIds] = useState<Map<string, SelectedAsset>>(new Map());
    const selectedAssetIdsRef = useRef<Map<string, SelectedAsset>>(new Map());
    selectedAssetIdsRef.current = selectedAssetIds;
    const assetIdToUuidRef = useRef<Map<string, string>>(new Map());
    const fetchSequenceRef = useRef(0);
    const tableRef = useRef(null);
    const activeFiltersRef = useRef<ActiveFilter[]>(getViewFilters());
    const subpage = React.useContext(subpageContext);
    const [isDataBearingBannerHidden, setIsDataBearingBannerHidden] = useState(false);
    const [bulkDeleteModalVisible, setBulkDeleteModalVisible] = useState(false);
    const [isBulkDeleting, setIsBulkDeleting] = useState(false);
    const [isBulkDeleteResultModalOpen, setIsBulkDeleteResultModalOpen] = useState(false);
    const [bulkDeleteResult, setBulkDeleteResult] = useState<{ deletedCount: number; failedCount: number } | undefined>(
        undefined
    );
    const hasDeleteAuth = userSessionService.userHasAllAuthorities([AUTH_ASSET_DELETE]);
    const hasFeatureLicense = userSessionService.hasFeatureLicense("FEATURE_ASSET_MANAGEMENT");
    const canDelete = hasDeleteAuth && hasFeatureLicense;
    const isOverDeleteLimit = selectedAssetIds.size > BULK_DELETE_LIMIT;
    const [isMoveToGroupModalOpen, setIsMoveToGroupModalOpen] = useState(false);
    const [moveToGroupAssetIds, setMoveToGroupAssetIds] = useState<string[]>([]);
    const isMobileViewport = useIsMobileViewport();
    const { setSubPage } = useSubPage();

    activeFiltersRef.current = activeFilters;

    useEffect(() => {
        if (searchInput === undefined && tableState.searchQuery === undefined) {
            return;
        }
        const debounceTimer = setTimeout(() => {
            setTableState((prev) => ({ ...prev, searchQuery: searchInput }));
        }, 1000);
        return () => clearTimeout(debounceTimer);
    }, [searchInput]);

    useEffect(() => {
        props.onSearchQueryChange?.(tableState.searchQuery);
        return () => props.onSearchQueryChange?.(undefined);
    }, [tableState.searchQuery]);

    useEffect(() => {
        const { availableColumns: columns, hiddenColumns: hidden } = computeColumns(props.columns);
        setAvailableColumns(columns);
        setHiddenColumns(hidden);
    }, [props.columns]);

    const handleHiddenColumnsChange = (newHiddenColumns: string[]) => {
        setHiddenColumns(newHiddenColumns);
        const hiddenSet = new Set(newHiddenColumns);
        const newVisibleKeys = availableColumns
            .filter((column) => !hiddenSet.has(column.key))
            .map((column) => column.key);
        const cleanedFilters = activeFilters.filter((activeFilter) => !hiddenSet.has(activeFilter.key));
        if (cleanedFilters.length !== activeFilters.length) {
            setActiveFilters(cleanedFilters);
            activeFiltersRef.current = cleanedFilters;
        }
        fetchData(
            true,
            {
                searchQuery: tableState.searchQuery,
                selectedRiskLevel: tableState.selectedRiskLevel,
                selectedRegion: tableState.selectedRegion,
                sortColumn: tableState.sortColumn,
                sortAscending: tableState.sortAscending,
            },
            newVisibleKeys,
            true
        );
    };

    const buildSearchRequest = (options: SearchRequestOptions): AssetSearchRequest => {
        const {
            cursor,
            columnKeys,
            sortAscending,
            sortColumn,
            searchQuery,
            selectedRiskLevel,
            selectedRegion,
            dataBearingFilterForRequest,
        } = options;

        const searchRequest: AssetSearchRequest = {
            cursor,
            paths: buildRequestedPaths(columnKeys.length > 0 ? columnKeys : DEFAULT_COLUMN_KEYS),
            size: PAGE_SIZE,
            sortAscending,
            sortColumn: sortColumn
                ? (availableColumns.find((column) => column.key === sortColumn)?.apiPath ?? sortColumn)
                : undefined,
            sortColumnType: FilterPathType.STRING,
            search: searchQuery || undefined,
            startDate: selectedDateRange.startDate,
            endDate: selectedDateRange.endDate,
            ...toViewFilterSelection(props.viewFilters ?? [], props.startDate, props.endDate),
            dataBearingFilter:
                dataBearingFilterForRequest !== DataBearingFilter.ALL ? dataBearingFilterForRequest : undefined,
        };

        const staticFilters: AssetFilter[] = [
            ...(selectedRiskLevel ? [{ name: "risk_level", value: selectedRiskLevel }] : []),
            ...(selectedRegion ? [{ name: "location", value: selectedRegion }] : []),
        ].map(({ name, value }) => ({
            name,
            value,
            operator: FilterOperator.EQUAL,
            pathType: FilterPathType.KEYWORD,
        }));

        const activeAssetFilters = activeFiltersRef.current.map(toAssetFilter);

        const allQuickFilters = [...staticFilters, ...activeAssetFilters];
        if (allQuickFilters.length > 0) {
            searchRequest.quickFilters = allQuickFilters;
        }

        if (props.viewId) {
            searchRequest.groupUuid = props.viewId;
        }

        return searchRequest;
    };

    const fetchData = async (
        reinitialized: boolean,
        overrides?: Partial<
            Pick<TableState, "selectedRiskLevel" | "selectedRegion" | "searchQuery" | "sortColumn" | "sortAscending">
        >,
        requestColumnKeys?: string[],
        backgroundRefetch?: boolean
    ) => {
        const sequence = ++fetchSequenceRef.current;

        if (!backgroundRefetch) {
            props.onTotalCountChange?.(undefined);
            if (reinitialized) {
                setInitialLoading(true);
                const emptyMap = new Map<string, SelectedAsset>();
                selectedAssetIdsRef.current = emptyMap;
                setSelectedAssetIds(emptyMap);
            } else {
                setLoading(true);
            }
        }

        const selectedRiskLevel = overrides?.selectedRiskLevel ?? tableState.selectedRiskLevel;
        const selectedRegion = overrides?.selectedRegion ?? tableState.selectedRegion;
        const searchQuery = overrides?.searchQuery ?? tableState.searchQuery;
        const sortColumn = overrides?.sortColumn ?? tableState.sortColumn;
        const sortAscending = overrides?.sortAscending ?? tableState.sortAscending;

        try {
            const searchRequest = buildSearchRequest({
                cursor: reinitialized ? undefined : tableState.cursor,
                columnKeys: requestColumnKeys ?? visibleColumnKeys,
                sortAscending,
                sortColumn,
                searchQuery,
                selectedRiskLevel,
                selectedRegion,
                dataBearingFilterForRequest: dataBearingFilter,
            });
            const abortController = new AbortController();
            abortControllers.push(abortController);
            const [response, existingKeysResponse] = await Promise.all([
                assetService.searchAssets(searchRequest, abortController),
                assetService.fetchExistingKeys(abortController).catch((error: Error) => {
                    if (error instanceof DOMException && error.name === "AbortError") {
                        throw error;
                    }
                    logger.warn(
                        "Failed to fetch existing asset keys, using only the keys of the returned assets.",
                        error
                    );
                    return { keys: [] };
                }),
            ]);

            if (sequence !== fetchSequenceRef.current) {
                return;
            }

            const dynamicColumnCandidates = [
                ...new Set([...Object.keys(response.assets[0] ?? {}), ...existingKeysResponse.keys]),
            ].sort((first, second) => first.localeCompare(second));

            setAvailableColumns((prev) => {
                const currentKeys = new Set(prev.map((column) => column.key));
                const knownApiPaths = new Set(prev.map((column) => column.apiPath));
                const extraColumns: AssetColumn[] = dynamicColumnCandidates
                    .filter(
                        (field) =>
                            !currentKeys.has(field) && !knownApiPaths.has(field) && !RESTRICTED_COLUMNS.includes(field)
                    )
                    .map((field) => ({
                        key: field,
                        label: formatDynamicColumnLabel(field),
                        visible: false,
                        apiPath: field,
                    }));
                if (extraColumns.length > 0) {
                    setHiddenColumns((prev) => [
                        ...prev,
                        ...extraColumns.map((column) => column.key).filter((key) => !prev.includes(key)),
                    ]);
                }
                return extraColumns.length > 0 ? [...prev, ...extraColumns] : prev;
            });

            setTableState((prev) => ({
                ...prev,
                data: response.assets.map((asset) => ({
                    ...asset,
                    checked: asset.assetId !== undefined && selectedAssetIdsRef.current.has(asset.assetId),
                })),
                totalAssetCount: response.totalAssets ?? response.assets.length,
                assetCount: response.assets.length,
                cursor: response.cursor,
                currentPage: reinitialized ? 1 : prev.currentPage,
                totalPages: response.totalPages ?? 0,
                scrollPosition: reinitialized ? undefined : prev.scrollPosition,
            }));

            props.onAssetsChange?.(response.assets);
            props.onTotalCountChange?.(response.totalAssets ?? response.assets.length);
            setTableTrackingSummary(response.trackingSummary);
            setRequestFailureMessage(undefined);
            const trackingController = new AbortController();
            abortControllers.push(trackingController);
            assetService
                .fetchTrackingSummary(trackingController)
                .then((summary) => {
                    dispatch(setTrackingSummary(summary));
                })
                .catch((err: Error) => {
                    logger.error("Failed to fetch tracking summary: ", err);
                });
        } catch (error) {
            if (error instanceof DOMException && error.name === "AbortError") {
                return;
            }
            logger.error("Failed to fetch assets.", error);
            setRequestFailureMessage(t("AssetManagement.requestFailed"));
        } finally {
            if (sequence === fetchSequenceRef.current && !backgroundRefetch) {
                setLoading(false);
                setInitialLoading(false);
            }
        }
    };

    const visibleColumnKeys = availableColumns
        .filter((column) => !hiddenColumns.includes(column.key))
        .map((column) => column.key);

    useEffect(() => {
        if (visibleColumnKeys.length > 0) {
            props.onVisibleColumnsChange?.(visibleColumnKeys);
        }
    }, [hiddenColumns, availableColumns]);

    useEffect(() => {
        fetchData(
            true,
            {
                searchQuery: tableState.searchQuery,
                selectedRiskLevel: tableState.selectedRiskLevel,
                selectedRegion: tableState.selectedRegion,
                sortColumn: tableState.sortColumn,
                sortAscending: tableState.sortAscending,
            },
            visibleColumnKeys.length > 0 ? visibleColumnKeys : props.columns
        );
    }, [
        tableState.searchQuery,
        tableState.selectedRiskLevel,
        tableState.selectedRegion,
        tableState.sortColumn,
        tableState.sortAscending,
        dataBearingFilter,
        props.refreshKey,
        selectedDateRange.startDate,
        selectedDateRange.endDate,
    ]);

    useEffect(() => {
        return () => {
            abortControllers.forEach((each) => {
                each.abort();
            });
        };
    }, []);

    const goToPage = async (page: number) => {
        setTableState((prev) => ({ ...prev, currentPage: page }));
        const sequence = ++fetchSequenceRef.current;
        setLoading(true);
        try {
            const searchRequest = buildSearchRequest({
                cursor: String(page - 1),
                columnKeys: visibleColumnKeys,
                sortAscending: tableState.sortAscending,
                sortColumn: tableState.sortColumn,
                searchQuery: tableState.searchQuery,
                selectedRiskLevel: tableState.selectedRiskLevel,
                selectedRegion: tableState.selectedRegion,
                dataBearingFilterForRequest: dataBearingFilter,
            });

            const response = await assetService.searchAssets(searchRequest);

            if (sequence !== fetchSequenceRef.current) {
                return;
            }

            setTableState((prev) => ({
                ...prev,
                data: response.assets.map((asset) => ({
                    ...asset,
                    checked: asset.assetId !== undefined && selectedAssetIdsRef.current.has(asset.assetId),
                })),
                totalAssetCount: response.totalAssets ?? response.assets.length,
                assetCount: response.assets.length,
                cursor: response.cursor,
                currentPage: page,
                totalPages: response.totalPages ?? 0,
                scrollPosition: 0,
            }));
            props.onAssetsChange?.(response.assets);
            props.onTotalCountChange?.(response.totalAssets ?? response.assets.length);
            setRequestFailureMessage(undefined);
        } catch (error) {
            logger.error("Error:", error);
            setRequestFailureMessage(t("AssetManagement.requestFailed"));
        } finally {
            setLoading(false);
        }
    };

    const findSelectedAssetIds = (): string[] => [...selectedAssetIds.keys()];

    const findSelectedAssetUuids = (): string[] => [...selectedAssetIds.values()].map(({ uuid }) => uuid);

    const hasAnyAssetSelected = (): boolean => selectedAssetIdsRef.current.size > 0;

    const anySelectedAssetBelongsToGroup = (): boolean => {
        if (!props.viewId) {
            return false;
        }
        return [...selectedAssetIds.values()].some(({ groupUuids }) => groupUuids?.includes(props.viewId!) === true);
    };

    const allSelectedAssetsHaveChildren = (): boolean =>
        findSelectedAssetIds().length > 0 &&
        findSelectedAssetIds().every((assetId) => {
            const selectedAsset = tableState.data.find((asset) => asset.assetId === assetId);
            return selectedAsset?.hasChildren === true || selectedAsset?.hasHiddenChildren === true;
        });

    const removeFromGroupTooltip: string | undefined =
        selectedAssetIds.size > REMOVE_FROM_GROUP_LIMIT
            ? t("AssetManagement.removeFromGroupLimitExceeded", {
                  limit: REMOVE_FROM_GROUP_LIMIT.toLocaleString(),
              })
            : !anySelectedAssetBelongsToGroup()
              ? t("AssetManagement.removeFromGroupDisabled")
              : undefined;

    const removeFromGroupButton = (
        <Button
            variant={"Tertiary"}
            testId={testIds.common.primaryView.table.actionBar.button}
            dataType={"REMOVE_FROM_GROUP"}
            icon={<Remove color={resolveColor("--theme-active-primary")} />}
            onClick={
                removeFromGroupTooltip === undefined
                    ? () => props.onRemoveFromGroup!(findSelectedAssetUuids())
                    : undefined
            }
            disabled={removeFromGroupTooltip !== undefined}
        >
            {t("AssetManagement.removeFromGroup")}
        </Button>
    );

    const hasAllAssetsSelected = (): boolean => {
        const nonDisabledAssets = tableState.data.filter((asset) => !asset.disabled);
        return (
            nonDisabledAssets.length > 0 &&
            nonDisabledAssets.every(
                (asset) => asset.assetId !== undefined && selectedAssetIdsRef.current.has(asset.assetId)
            )
        );
    };

    const updateAssetState = (checkedValue: boolean, selectedId?: string) => {
        setSelectedAssetIds((prev) => {
            const updatedIds = new Map(prev);
            if (selectedId !== undefined) {
                if (checkedValue) {
                    const asset = tableState.data.find((asset) => asset.assetId === selectedId);
                    if (asset !== undefined) {
                        updatedIds.set(selectedId, { uuid: asset.uuid, groupUuids: asset.groupUuids });
                    }
                } else {
                    updatedIds.delete(selectedId);
                }
            } else {
                tableState.data
                    .filter((asset) => !asset.disabled)
                    .forEach((asset) => {
                        const { assetId, uuid, groupUuids } = asset;
                        if (assetId === undefined) {
                            return;
                        }
                        if (checkedValue) {
                            updatedIds.set(assetId, { uuid, groupUuids });
                        } else {
                            updatedIds.delete(assetId);
                            assetIdToUuidRef.current.delete(assetId);
                        }
                    });
            }
            selectedAssetIdsRef.current = updatedIds;
            return updatedIds;
        });

        setTableState((prev) => ({
            ...prev,
            data: prev.data.map((asset) => {
                if (selectedId !== undefined) {
                    return asset.assetId === selectedId ? { ...asset, checked: checkedValue } : asset;
                }
                return asset.disabled ? asset : { ...asset, checked: checkedValue };
            }),
        }));
    };

    const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>, id: string) => {
        updateAssetState(event.target.checked, id);
    };

    const handleBulkDelete = () => {
        setIsBulkDeleting(true);
        setBulkDeleteModalVisible(false);
        const abortController = new AbortController();
        abortControllers.push(abortController);
        const uuids = findSelectedAssetUuids();
        assetService
            .bulkDeleteAssets(uuids, abortController)
            .then((response) => {
                setIsBulkDeleting(false);
                if (response.failedAssetUuids && response.failedAssetUuids.length > 0) {
                    setBulkDeleteResult({
                        deletedCount: uuids.length - response.failedAssetUuids.length,
                        failedCount: response.failedAssetUuids.length,
                    });
                    setIsBulkDeleteResultModalOpen(true);
                } else {
                    showToast(
                        t("AssetManagement.bulkDeleteAssets.successMessage", {
                            count: uuids.length,
                        }),
                        "success"
                    );
                }
                const emptyMap = new Map<string, SelectedAsset>();
                selectedAssetIdsRef.current = emptyMap;
                setSelectedAssetIds(emptyMap);
                assetIdToUuidRef.current.clear();
                fetchData(true, {
                    searchQuery: tableState.searchQuery,
                    selectedRiskLevel: tableState.selectedRiskLevel,
                    selectedRegion: tableState.selectedRegion,
                    sortColumn: tableState.sortColumn,
                    sortAscending: tableState.sortAscending,
                });
            })
            .catch(() => {
                if (!abortController.signal.aborted) {
                    setBulkDeleteModalVisible(false);
                    setIsBulkDeleting(false);
                    setBulkDeleteResult({ deletedCount: 0, failedCount: uuids.length });
                    setIsBulkDeleteResultModalOpen(true);
                }
            });
    };

    const createSelectionCheckbox = (id: string, checked: boolean) => {
        return (
            <Checkbox
                data-testid={testIds.common.primaryView.table.selectRowCheckbox}
                name="assetIds"
                id={id}
                checked={checked}
                value={id}
                hideLabelSection={true}
                onChange={(event) => handleCheckboxChange(event, id)}
            />
        );
    };

    const createFirstColumn = (): Column<Asset> => {
        return {
            Header: () => (
                <Checkbox
                    data-testid={testIds.common.primaryView.table.selectAllCheckbox}
                    checked={hasAllAssetsSelected()}
                    indeterminate={hasAnyAssetSelected() && !hasAllAssetsSelected()}
                    hideLabelSection={true}
                    onChange={(event) => {
                        updateAssetState(event.target.checked ? true : !hasAllAssetsSelected());
                    }}
                    name="assetCheckbox"
                />
            ),
            accessor: "checked",
            disableSortBy: true,
            disableResizing: true,
            minWidth: 70,
            maxWidth: 70,

            Cell: ({
                cell: {
                    row: { original: asset },
                },
            }: {
                cell: { row: { original: Asset } };
            }) => {
                return (
                    <Box
                        verticalAlignment="Center"
                        gap={"Xxs"}
                        onClick={(event) => event.stopPropagation()}
                        className={style.firstCell}
                    >
                        {asset.assetId !== undefined && createSelectionCheckbox(asset.assetId, asset.checked === true)}

                        {asset.parentUuid === undefined && (
                            <AssetKebabMenu
                                tableRef={tableRef}
                                asset={asset}
                                isDeleteAllowed={!asset.hasChildren && !asset.hasHiddenChildren}
                                deleteDisableTooltip={deduceDeleteDisableTooltipMessage(
                                    asset.hasChildren,
                                    asset.hasHiddenChildren
                                )}
                                onAssetEdit={(updated) => {
                                    setTableState((prev) => ({
                                        ...prev,
                                        data: prev.data.map((asset) =>
                                            asset.uuid === updated.uuid ? { ...asset, ...updated } : asset
                                        ),
                                    }));
                                }}
                                onAssetDelete={() => {
                                    setIsSidePanelOpen(false);
                                    setSelectedAsset(undefined);
                                    fetchData(true, {
                                        searchQuery: tableState.searchQuery,
                                        selectedRiskLevel: tableState.selectedRiskLevel,
                                        selectedRegion: tableState.selectedRegion,
                                        sortColumn: tableState.sortColumn,
                                        sortAscending: tableState.sortAscending,
                                    });
                                }}
                            />
                        )}
                    </Box>
                );
            },
        };
    };

    const deduceDeleteDisableTooltipMessage = (
        hasChildren: boolean | undefined,
        hasHiddenChildren: boolean | undefined
    ) => {
        return hasChildren
            ? t("AssetManagement.deleteAsset.hasSubAssetsMessage")
            : hasHiddenChildren
              ? t("AssetManagement.deleteAsset.hasUntrackedSubAssetsMessage")
              : undefined;
    };

    const renderWithDataBearingIcon = (
        content: React.ReactNode,
        isDataBearing: boolean | undefined
    ): React.ReactNode => (
        <Box verticalAlignment="Center" gap="Xxs">
            <div className={style.dataBearingCellText}>{content}</div>
            <div className={style.dataBearingIconSlot}>{isDataBearing && <DataBearingIcon />}</div>
        </Box>
    );

    const openAssetSidePanel = (asset: Asset) => {
        if (!asset.uuid) {
            return;
        }
        assetDetailsAbortControllerRef.current?.abort();
        const abortController = new AbortController();
        assetDetailsAbortControllerRef.current = abortController;
        abortControllers.push(abortController);
        setSelectedAsset(asset);
        setIsSidePanelOpen(true);
        setAssetDetailsLoading(true);

        assetService
            .fetchAssetByUuid(asset.uuid, abortController)
            .then((assetDetails) => {
                if (!abortController.signal.aborted) {
                    setSelectedAsset(assetDetails);
                    setAssetDetailsLoading(false);
                }
            })
            .catch((error) => {
                if (!abortController.signal.aborted) {
                    logger.error("Failed to fetch asset details by uuid.", error);
                    setRequestFailureMessage(t("AssetManagement.requestFailed"));
                    setAssetDetailsLoading(false);
                }
            });
    };

    const createIdColumn = (): Column<Asset> => {
        const removeAssetFromTree = (assets: Asset[], uuid: string): Asset[] =>
            assets
                .filter((asset) => asset.uuid !== uuid)
                .map((asset) => {
                    if (asset.children?.length) {
                        return { ...asset, children: removeAssetFromTree(asset.children, uuid) };
                    }
                    return asset;
                });
        const updateAssetInTree = (assets: Asset[], updated: Asset): Asset[] =>
            assets.map((asset) => {
                if (asset.uuid === updated.uuid) {
                    return { ...asset, ...updated };
                }
                if (asset.children?.length) {
                    return { ...asset, children: updateAssetInTree(asset.children, updated) };
                }
                return asset;
            });

        const setChildrenForUuid = (assets: Asset[], targetUuid: string, children: Asset[]): Asset[] =>
            assets.map((asset) => {
                if (asset.uuid === targetUuid) {
                    return { ...asset, children };
                }
                if (asset.children) {
                    return { ...asset, children: setChildrenForUuid(asset.children, targetUuid, children) };
                }
                return asset;
            });

        return {
            Header: () => <TextWithTooltip text={t("AssetManagement.assetId")} key="assetId" />,
            accessor: "assetId",
            Cell: ({ cell: { value }, row }: CellProps<Asset>) => {
                const asset = row.original;
                if (typeof value !== "string") {
                    return;
                }
                const colorKey = getConditionalCellColorKey(conditionalRules, AssetPathKey.ASSET_ID, value);
                const renderLink = (onClick?: () => void) => (
                    <LinkText as={"button"} onClick={onClick}>
                        {value}
                    </LinkText>
                );

                const isChildAsset = asset.parentUuid !== undefined;
                const hasChildrenToExpand = !!(asset.hasChildren || asset.children?.length);

                if (!isChildAsset && !hasChildrenToExpand) {
                    return renderWithHighlight(colorKey, renderLink());
                }

                return renderWithHighlight(
                    colorKey,
                    <Box
                        verticalAlignment="Center"
                        gap="Xxs"
                        onClick={(e) => {
                            e.stopPropagation();
                        }}
                    >
                        {isChildAsset && (
                            <AssetKebabMenu
                                tableRef={tableRef}
                                asset={asset}
                                isDeleteAllowed={!asset.hasChildren && !asset.hasHiddenChildren}
                                deleteDisableTooltip={deduceDeleteDisableTooltipMessage(
                                    asset.hasChildren,
                                    asset.hasHiddenChildren
                                )}
                                onAssetEdit={(updated) => {
                                    setTableState((prev) => ({
                                        ...prev,
                                        data: updateAssetInTree(prev.data, updated),
                                    }));
                                }}
                                onAssetDelete={() => {
                                    setIsSidePanelOpen(false);
                                    setSelectedAsset(undefined);
                                    setTableState((prev) => ({
                                        ...prev,
                                        data: removeAssetFromTree(prev.data, asset.uuid),
                                    }));
                                }}
                            />
                        )}
                        {hasChildrenToExpand && (
                            <button
                                className={style.expandToggle}
                                onClick={(error) => {
                                    error.stopPropagation();
                                    setIsSidePanelOpen(false);
                                    setSelectedAsset(undefined);
                                    if (!row.isExpanded && !asset.children?.length) {
                                        fetchChildren(asset.uuid, visibleColumnKeys, {
                                            searchQuery: tableState.searchQuery,
                                            sortColumnApiPath: availableColumns.find(
                                                (column) => column.key === tableState.sortColumn
                                            )?.apiPath,
                                            sortAscending: tableState.sortAscending,
                                            filters: activeFiltersRef.current.map(toAssetFilter),
                                            filterLogic:
                                                activeFiltersRef.current.length > 1
                                                    ? Array.from(
                                                          {
                                                              length: activeFiltersRef.current.length,
                                                          },
                                                          (_, number) => String(number)
                                                      ).join(" AND ")
                                                    : undefined,
                                        })
                                            .then((children) => {
                                                setTableState((previuous) => ({
                                                    ...previuous,
                                                    data: setChildrenForUuid(previuous.data, asset.uuid, children),
                                                }));
                                            })
                                            .catch((error) => {
                                                logger.error("Failed to fetch child assets.", error);
                                            });
                                    }
                                    row.toggleRowExpanded();
                                }}
                                aria-expanded={row.isExpanded}
                            >
                                <ExpandArrow isExpanded={row.isExpanded} />
                            </button>
                        )}
                        {renderLink(() => openAssetSidePanel(asset))}
                    </Box>
                );
            },
        };
    };
    const createRiskLevelColumn = (): Column<Asset> => {
        return {
            Header: () => <TextWithTooltip text={translatedPaths(AssetPathKey.RISK_LEVEL)} key="riskLevel" />,
            accessor: "riskLevel",
            Cell: ({ cell: { value } }: { cell: { value: AssetFieldValue }; row: { original: Asset } }) => {
                if (typeof value !== "string") {
                    return null;
                }
                const colorKey = getConditionalCellColorKey(conditionalRules, AssetPathKey.RISK_LEVEL, value);
                const content = (
                    <StatusBadge value={deduceRiskLevelValue(t, value)} status={getRiskLevelStatus(value)} />
                );
                return renderWithHighlight(colorKey, content);
            },
        };
    };

    const createGradeColumn = (key: string, label: string, apiPath: string): Column<Asset> => {
        return {
            Header: () => <TextWithTooltip text={label} key={key} />,
            accessor: key as keyof Asset,
            Cell: ({ cell: { value } }: CellProps<Asset>) => {
                if (!isGrade(value)) {
                    return null;
                }
                const colorKey = getConditionalCellColorKey(conditionalRules, apiPath, value);
                return renderWithHighlight(colorKey, <GradeBadge grade={value} bold />);
            },
        };
    };

    const createValueColumn = (key: string, label: string): Column<Asset> => {
        return {
            Header: () => <TextWithTooltip text={label} key={key} />,
            accessor: key as keyof Asset,
            Cell: ({ cell: { value } }: CellProps<Asset>) => formatAssetValue(value) ?? null,
        };
    };

    const createTextColumn = (key: string, label: string, apiPath: string): Column<Asset> => {
        const isAssetType = key === "assetType";
        return {
            Header: () => <TextWithTooltip text={label} key={key} />,
            accessor: key as keyof Asset,
            Cell: ({ cell: { value }, row: { original: asset } }: CellProps<Asset>) => {
                const colorKey =
                    typeof value === "string"
                        ? getConditionalCellColorKey(conditionalRules, apiPath, value)
                        : undefined;
                const cell = isAssetType ? renderWithDataBearingIcon(value, asset.isDataBearing) : value;
                return renderWithHighlight(colorKey, cell);
            },
        };
    };

    const createGradingActionColumn = (key: string, label: string, apiPath: string): Column<Asset> => {
        return {
            Header: () => <TextWithTooltip text={label} key={key} />,
            accessor: key as keyof Asset,
            Cell: ({ cell: { value } }: CellProps<Asset>) => {
                const translationKey = getGradingActionTranslationKey(value);
                // Show unrecognized values as they are rather than hiding them.
                const displayValue = translationKey === undefined ? value : t(translationKey);
                const colorKey =
                    typeof value === "string"
                        ? getConditionalCellColorKey(conditionalRules, apiPath, value)
                        : undefined;
                return renderWithHighlight(colorKey, displayValue);
            },
        };
    };

    const createTimestampColumn = (key: string, label: string): Column<Asset> => {
        return {
            Header: label,
            accessor: key as keyof Asset,
            Cell: ({ cell: { value } }: CellProps<Asset>) =>
                typeof value === "string" && value !== "" ? formatTimestamp(value) : value,
        };
    };

    const createErasabilityColumn = (key: string, label: string, apiPath: string): Column<Asset> => {
        return {
            Header: () => (
                <Box verticalAlignment="Center" gap={"Xxs"}>
                    <Tooltip>
                        <TooltipTrigger>
                            <Info size={16} />
                        </TooltipTrigger>
                        <TooltipContent>{t("AssetManagement.table.erasureProbabilityTooltip")}</TooltipContent>
                    </Tooltip>
                    {label}
                </Box>
            ),
            accessor: key as keyof Asset,
            Cell: ({ cell: { value }, row: { original: asset } }: CellProps<Asset>) => {
                if (asset.detailsTruncated === true && value == null) {
                    return <StatusBadge value="" status={Status.NEUTRAL} />;
                }
                const displayValue = value != null ? `${value}%` : null;
                const colorKey =
                    typeof value === "string" || typeof value === "number"
                        ? getConditionalCellColorKey(conditionalRules, apiPath, String(value))
                        : undefined;
                return renderWithHighlight(colorKey, displayValue);
            },
        };
    };

    const createDurationColumn = (key: string, label: string, apiPath: string): Column<Asset> => {
        return {
            Header: () => (
                <Box verticalAlignment="Center" gap={"Xxs"}>
                    <Tooltip>
                        <TooltipTrigger>
                            <Info size={16} />
                        </TooltipTrigger>
                        <TooltipContent>{t("AssetManagement.table.erasureDurationTooltip")}</TooltipContent>
                    </Tooltip>
                    {label}
                </Box>
            ),
            accessor: key as keyof Asset,
            Cell: ({ cell: { value }, row: { original: asset } }: CellProps<Asset>) => {
                if (asset.detailsTruncated === true && value == null) {
                    return <StatusBadge value="" status={Status.NEUTRAL} />;
                }
                const seconds = typeof value === "number" ? value : Number(value);
                const displayValue =
                    !isNaN(seconds) && value !== "" && value != null ? formatDurationSeconds(seconds) : null;
                const colorKey =
                    typeof value === "string" || typeof value === "number"
                        ? getConditionalCellColorKey(conditionalRules, apiPath, String(value))
                        : undefined;
                return renderWithHighlight(colorKey, displayValue);
            },
        };
    };

    const createColumns = (): Column<Asset>[] => {
        const columns: Column<Asset>[] = [];

        columns.push(createFirstColumn());

        availableColumns.forEach((assetColumn) => {
            switch (assetColumn.apiPath) {
                case AssetPathKey.ASSET_ID:
                    columns.push(createIdColumn());
                    break;
                case AssetPathKey.RISK_LEVEL:
                    columns.push(createRiskLevelColumn());
                    break;
                case AssetPathKey.BLANCCO_ERASABILITY:
                    columns.push(
                        createErasabilityColumn(
                            assetColumn.key,
                            translatedPaths(assetColumn.apiPath),
                            assetColumn.apiPath
                        )
                    );
                    break;
                case AssetPathKey.BLANCCO_DURATION:
                    columns.push(
                        createDurationColumn(assetColumn.key, translatedPaths(assetColumn.apiPath), assetColumn.apiPath)
                    );
                    break;
                case AssetPathKey.CREATED_AT:
                    columns.push(createTimestampColumn(assetColumn.key, translatedPaths(assetColumn.apiPath)));
                    break;
                case AssetPathKey.BLANCCO_GRADE:
                    columns.push(
                        createGradeColumn(assetColumn.key, translatedPaths(assetColumn.apiPath), assetColumn.apiPath)
                    );
                    break;
                case AssetPathKey.GRADING_ACTION:
                    columns.push(
                        createGradingActionColumn(
                            assetColumn.key,
                            translatedPaths(assetColumn.apiPath),
                            assetColumn.apiPath
                        )
                    );
                    break;
                case AssetPathKey.ASSET_VALUE:
                case AssetPathKey.POTENTIAL_UPLIFT_VALUE:
                case AssetPathKey.TOTAL_VALUE:
                    columns.push(createValueColumn(assetColumn.key, translatedPaths(assetColumn.apiPath)));
                    break;
                default:
                    columns.push(
                        createTextColumn(assetColumn.key, translatedPaths(assetColumn.apiPath), assetColumn.apiPath)
                    );
            }
        });

        return columns;
    };

    let emptyMessage: string | undefined;
    if (!requestFailureMessage && !loading) {
        emptyMessage = !tableState.searchQuery
            ? t("AssetManagement.noAssetsFound")
            : t("Common.searchEmptyStateMessage", { searchQuery: tableState.searchQuery });
    }

    const createBottomFilterContainer = (
        filter: DataBearingFilter,
        toShowIcon: boolean,
        label: string,
        numberOfAssets: number
    ) => {
        const badge =
            DataBearingFilter.ALL === filter ? (
                <TextBadge text={label} className={style.allAssetsBadge} />
            ) : toShowIcon ? (
                <BmpStatusBadge values={[{ status: BmpStatus.INFO, title: label }]} icon={<DataBearingIcon />} />
            ) : (
                <StatusBadge value={label} status={Status.NEUTRAL} />
            );
        return (
            <Box
                key={filter}
                verticalAlignment={"Center"}
                onClick={() => setDataBearingFilter(filter)}
                className={classNames(
                    style.dataBearingFilterBox,
                    filter === dataBearingFilter ? style.boxActive : style.boxInactive
                )}
                gap={"Xxs"}
            >
                {badge}

                {t("AssetManagement.dataBearingFilter.assetsCount", {
                    count: numberOfAssets,
                })}
            </Box>
        );
    };

    const createContextualBanner = (variant: "Warning" | "Error", content: React.ReactNode) => {
        return (
            <Box columnLayout paddingBottom={"Sm"}>
                <ContextualBanner variant={variant} className={style.dataBearingBanner}>
                    <Box verticalAlignment="Center" justifyContent="SpaceBetween" flexWrap="Wrap" gap="Xxxs">
                        {content}
                        <Button
                            variant="Tertiary"
                            onClick={() =>
                                window.open(
                                    "https://portal.servicedesk.blancco.com/servicedesk/customer/user/login?destination=portals",
                                    "_blank"
                                )
                            }
                        >
                            {t("Common.contactBlanccoSupport")}
                        </Button>
                    </Box>
                    <IconButton
                        onClick={() => setIsDataBearingBannerHidden(true)}
                        className={style.dataBearingBannerCloseIcon}
                    >
                        <Close />
                    </IconButton>
                </ContextualBanner>
            </Box>
        );
    };

    const createDataBearingLimitBanner = () => {
        if (!globalTrackingSummary || isDataBearingBannerHidden) {
            return null;
        }

        const reached = globalTrackingSummary.trackingLimit - globalTrackingSummary.trackedCount === 0;

        if (reached) {
            const content = (
                <Box flexWrap="Wrap" verticalAlignment="Center">
                    <strong>{t("AssetManagement.dataBearingFilter.limitReached")}</strong>
                    {"\u00A0-\u00A0"}
                    {t("AssetManagement.dataBearingFilter.assetsOnHold", {
                        count: globalTrackingSummary.allDataBearingCount - globalTrackingSummary.trackedCount,
                    })}
                </Box>
            );
            return createContextualBanner("Error", content);
        }

        const approaching = isLimitApproaching(
            globalTrackingSummary.trackingLimit - globalTrackingSummary.trackedCount,
            globalTrackingSummary.trackingLimit
        );

        if (approaching) {
            const content = (
                <Box flexWrap="Wrap" verticalAlignment="Center">
                    <strong>{t("AssetManagement.dataBearingFilter.limitApproaching")}</strong>
                    {"\u00A0-\u00A0"}
                    {t("AssetManagement.dataBearingFilter.assetsRemaining", {
                        count: globalTrackingSummary.trackingLimit - globalTrackingSummary.trackedCount,
                    })}
                </Box>
            );

            return createContextualBanner("Warning", content);
        }

        return null;
    };

    return (
        <Box columnLayout className={style.tableContainer} testId={props.testId}>
            {createDataBearingLimitBanner()}
            <Box paddingBottom={"Sm"} className={style.tableActions}>
                <Box gap={"Sm"} flexWrap={"Wrap"} verticalAlignment={"Center"}>
                    {isMobileViewport &&
                        hasFeatureLicense &&
                        userSessionService.userHasAllAuthorities([AUTH_ASSET_CREATE]) && (
                            <Button
                                variant="Primary"
                                small
                                icon={<Add />}
                                onClick={() =>
                                    setSubPage(
                                        <ManageAssetView groupUuid={props.viewId} onCreateDone={props.onAssetCreate} />
                                    )
                                }
                                testId={testIds.workArea.asset.createAssetButton}
                                className={style.createAssetButton}
                            >
                                {t("AssetManagement.createAsset")}
                            </Button>
                        )}

                    <SearchBar
                        onSearch={(query: string) => setSearchInput(query.trim() || undefined)}
                        onClear={() => {
                            setSearchInput(undefined);
                            setTableState((prev) => ({ ...prev, searchQuery: undefined }));
                        }}
                        placeholder={t("Common.search")}
                        hideSearchIcon={loading}
                        value={searchInput}
                        disabled={initialLoading}
                        hideLabelSection={true}
                        testIds={{
                            itself: testIds.common.searchInput.itself,
                            searchButton: testIds.common.searchInput.searchButton,
                            clearButton: testIds.common.searchInput.clearButton,
                        }}
                        name={"searchBox"}
                        className={style.searchBar}
                    />
                    <Divider direction={"Vertical"} className={classNames(style.dividerHeight, style.hiddenOnMobile)} />
                    <Box gap={"Xs"} flexWrap={"Wrap"} verticalAlignment={"Center"} className={style.hiddenOnMobile}>
                        <span className={style.hiddenOnMobile}>{translatedPaths(AssetPathKey.RISK_LEVEL)}:</span>
                        {[undefined, ...assetService.getRiskLevels()].map((level) => (
                            <QuickFilterButton
                                key={level ?? "all"}
                                isActive={tableState.selectedRiskLevel === level}
                                onClick={() => {
                                    const quickFilters = level
                                        ? [{ name: "risk_level", value: level, operator: FilterOperator.EQUAL }]
                                        : [];
                                    const controlFilters = activeFiltersRef.current.map(toAssetFilter);
                                    props.onFiltersChange?.([...quickFilters, ...controlFilters]);
                                    setTableState((prev) => ({
                                        ...prev,
                                        cursor: undefined,
                                        selectedRiskLevel: level,
                                    }));
                                }}
                                testId={testIds.common.primaryView.table.filter.button}
                                dataType={level?.toUpperCase() ?? "ALL"}
                            >
                                {!level ? t("AssetManagement.all") : deduceRiskLevelValue(t, level)}
                            </QuickFilterButton>
                        ))}
                    </Box>
                    <Divider direction={"Vertical"} className={classNames(style.dividerHeight, style.hiddenOnMobile)} />

                    <Box verticalAlignment={"Center"} className={style.quickFilterAndDatePickerAndColumnSelector}>
                        <Box flexWrap={"Wrap"} verticalAlignment={"Center"} className={style.filterDropdownWrapper}>
                            <FilterDropdown
                                columns={availableColumns.filter((column) => !hiddenColumns.includes(column.key))}
                                activeFilters={activeFilters}
                                onFiltersChange={(filters) => {
                                    setActiveFilters(filters);
                                    activeFiltersRef.current = filters;
                                    if (filters.length === 0) {
                                        removeViewFilters();
                                    }
                                    props.onFiltersChange?.(filters.map(toAssetFilter));
                                    fetchData(true, {
                                        searchQuery: tableState.searchQuery,
                                        selectedRiskLevel: tableState.selectedRiskLevel,
                                        selectedRegion: tableState.selectedRegion,
                                        sortColumn: tableState.sortColumn,
                                        sortAscending: tableState.sortAscending,
                                    });
                                }}
                                onSaveFilters={() => saveViewFilters(activeFilters)}
                                onCreateAssetGroup={
                                    !props.viewId
                                        ? () => {
                                              const preselectedFilters: AssetCustomViewFilterEntry[] =
                                                  activeFilters.map((field) => ({
                                                      field: field.apiPath as AssetPathKey,
                                                      operator: (DISPLAY_TO_FILTER_OPERATOR[field.operatorKey] ??
                                                          FilterOperator.MATCH) as FilterOperator,
                                                      value: field.value,
                                                      logicOperator: "AND",
                                                  }));
                                              subpage?.setSubpage({
                                                  content: (
                                                      <CreateAssetCustomViewSubpage
                                                          initialFilters={preselectedFilters}
                                                          dateRange={
                                                              selectedDateRange.startDate && selectedDateRange.endDate
                                                                  ? [
                                                                        selectedDateRange.startDate,
                                                                        selectedDateRange.endDate,
                                                                    ]
                                                                  : undefined
                                                          }
                                                          folders={(assetViews ?? []).filter((view) => view.folder)}
                                                      />
                                                  ),
                                                  isModal: false,
                                              });
                                          }
                                        : undefined
                                }
                            />
                        </Box>
                        <Box
                            gap={"Xxs"}
                            flexWrap={"NoWrap"}
                            verticalAlignment={"Center"}
                            className={style.datePickerAndColumnSelector}
                        >
                            <div className={style.datePicker}>
                                <CustomDatePickerView
                                    onUpdate={(startDate, endDate) => {
                                        setSelectedDateRange({ startDate, endDate });
                                    }}
                                    defaultDateRange={
                                        selectedDateRange.startDate && selectedDateRange.endDate
                                            ? [
                                                  new Date(selectedDateRange.startDate),
                                                  new Date(selectedDateRange.endDate),
                                              ]
                                            : undefined
                                    }
                                    defaultPlaceholder={false}
                                    category={Category.ASSET_MANAGEMENT}
                                    editable
                                />
                            </div>
                            {availableColumns.length > 0 && (
                                <AssetColumnSelector
                                    key={props.viewName ?? "default"}
                                    setHiddenColumns={handleHiddenColumnsChange}
                                    columns={availableColumns.filter(
                                        (column) => column.apiPath !== AssetPathKey.ASSET_ID
                                    )}
                                    hiddenColumns={hiddenColumns}
                                />
                            )}
                        </Box>
                    </Box>
                </Box>
            </Box>
            <div className={style.tableInnerContainer} ref={tableRef}>
                <Table
                    columns={createColumns()}
                    hiddenColumns={hiddenColumns}
                    data={tableState.data}
                    getSubRows={(row) => row.children}
                    loaded={!initialLoading && !loading}
                    emptyStateMessage={emptyMessage ?? ""}
                    failureMessage={requestFailureMessage}
                    tableIdentity={RepositoryKey.ALL_ASSETS_VIEW_TABLE}
                    tooltips={true}
                    scrollTo={tableState.scrollPosition}
                    testIds={{ itself: testIds.workArea.asset.table.itself }}
                    isSidePanelOpen={isSidePanelOpen}
                    header={true}
                    sidePanel={
                        selectedAsset ? (
                            <AssetSidePanel
                                asset={selectedAsset}
                                onClose={() => {
                                    setIsSidePanelOpen(false);
                                    setSelectedAsset(undefined);
                                }}
                                isLoading={isAssetDetailsLoading}
                                onAssetUpdated={(updated) =>
                                    setSelectedAsset((previous) =>
                                        previous?.uuid === updated.uuid ? updated : previous
                                    )
                                }
                            />
                        ) : (
                            <></>
                        )
                    }
                    sortCallbacks={{
                        onChange: (column, ascending) => {
                            setTableState((prev) => {
                                const nextAscending = ascending ?? prev.sortAscending;

                                if (prev.sortColumn === column && prev.sortAscending === nextAscending) {
                                    return prev;
                                }

                                return {
                                    ...prev,
                                    sortColumn: column,
                                    sortAscending: nextAscending,
                                };
                            });
                        },
                        onDisable: () => {
                            setTableState((prev) => {
                                if (prev.sortColumn === "") {
                                    return prev;
                                }

                                return {
                                    ...prev,
                                    sortColumn: "",
                                };
                            });
                        },
                    }}
                    onOpenSidePanel={openAssetSidePanel}
                    onCloseSidePanel={() => {
                        setIsSidePanelOpen(false);
                        setSelectedAsset(undefined);
                    }}
                />
                {hasAnyAssetSelected() && (
                    <ActionBar data-testid={testIds.common.primaryView.table.actionBar.itself}>
                        {t("AssetManagement.assetsSelected", {
                            total: selectedAssetIds.size,
                        })}

                        <ActionBarSeparator />

                        <Button
                            variant="Tertiary"
                            testId={testIds.common.primaryView.table.actionBar.deselectButton}
                            onClick={() => {
                                const empty = new Map<string, SelectedAsset>();
                                selectedAssetIdsRef.current = empty;
                                setSelectedAssetIds(empty);
                                setTableState((prev) => ({
                                    ...prev,
                                    data: prev.data.map((asset) => ({ ...asset, checked: false })),
                                }));
                            }}
                        >
                            {t("AssetManagement.deselect")}
                        </Button>

                        <Button
                            variant="Tertiary"
                            testId={testIds.common.primaryView.table.actionBar.selectAllButton}
                            onClick={() => updateAssetState(true)}
                        >
                            {tableState.assetCount > 0
                                ? t("AssetManagement.selectAll", { total: tableState.assetCount })
                                : t("Common.multiSelect.selectAll")}
                        </Button>

                        <ActionBarSeparator />
                        <Button
                            variant={"Tertiary"}
                            testId={testIds.common.primaryView.table.actionBar.button}
                            dataType={"CREATE_GROUP"}
                            icon={<Add />}
                            onClick={() => {
                                const selectedIds = [...selectedAssetIds.keys()];
                                const preselectedFilters: AssetCustomViewFilterEntry[] =
                                    selectedIds.length > 0
                                        ? [
                                              {
                                                  field: AssetPathKey.ASSET_ID,
                                                  operator: FilterOperator.IN,
                                                  value: selectedIds.join(","),
                                                  logicOperator: "AND",
                                              },
                                          ]
                                        : [];
                                subpage?.setSubpage({
                                    content: (
                                        <CreateAssetCustomViewSubpage
                                            initialFilters={preselectedFilters}
                                            dateRange={
                                                selectedDateRange.startDate && selectedDateRange.endDate
                                                    ? [selectedDateRange.startDate, selectedDateRange.endDate]
                                                    : undefined
                                            }
                                            selectedAssetIds={findSelectedAssetUuids()}
                                            folders={(assetViews ?? []).filter((view) => view.folder)}
                                        />
                                    ),
                                    isModal: false,
                                });
                            }}
                        >
                            {t("AssetManagement.createAssetGroup")}
                        </Button>

                        {props.toShowRemoveFromGroup &&
                            userSessionService.userHasAllAuthorities([AUTH_CUSTOM_ASSET_VIEW_EDIT]) &&
                            (removeFromGroupTooltip !== undefined ? (
                                <Tooltip>
                                    <TooltipContent>{removeFromGroupTooltip}</TooltipContent>
                                    <TooltipTrigger>
                                        <span>{removeFromGroupButton}</span>
                                    </TooltipTrigger>
                                </Tooltip>
                            ) : (
                                removeFromGroupButton
                            ))}

                        <Button
                            variant={"Tertiary"}
                            testId={testIds.common.primaryView.table.actionBar.button}
                            dataType={"ADD_ASSET_TO_GROUP"}
                            icon={<View />}
                            onClick={() => {
                                setMoveToGroupAssetIds(findSelectedAssetUuids());
                                setIsMoveToGroupModalOpen(true);
                            }}
                        >
                            {t("AssetManagement.addAssetsToGroup")}
                        </Button>

                        {hasFeatureLicense &&
                            userSessionService.userHasAnyAuthority([AUTH_ASSET_VIEW, AUTH_CUSTOM_ASSET_VIEW_VIEW]) && (
                                <Button
                                    variant="Tertiary"
                                    testId={testIds.common.primaryView.table.actionBar.button}
                                    dataType="EXPORT"
                                    icon={<Export />}
                                    onClick={() => {
                                        props.onExportSelected?.(findSelectedAssetIds(), visibleColumnKeys);
                                    }}
                                >
                                    {t("Common.export")}
                                </Button>
                            )}

                        <ActionBarSeparator />

                        {canDelete && (
                            <>
                                <Tooltip>
                                    <TooltipTrigger>
                                        <Button
                                            variant="TertiaryDelete"
                                            icon={<Delete />}
                                            testId={testIds.common.primaryView.table.actionBar.button}
                                            dataType="DELETE"
                                            disabled={
                                                isOverDeleteLimit || isBulkDeleting || allSelectedAssetsHaveChildren()
                                            }
                                            onClick={() => setBulkDeleteModalVisible(true)}
                                        >
                                            {t("Common.delete")}
                                        </Button>
                                    </TooltipTrigger>
                                    {(isOverDeleteLimit || allSelectedAssetsHaveChildren()) && (
                                        <TooltipContent>
                                            {isOverDeleteLimit
                                                ? t("AssetManagement.bulkDeleteAssets.limitTooltip", {
                                                      max: BULK_DELETE_LIMIT,
                                                  })
                                                : t("AssetManagement.bulkDeleteAssets.hasChildrenTooltip")}
                                        </TooltipContent>
                                    )}
                                </Tooltip>
                            </>
                        )}
                    </ActionBar>
                )}
            </div>

            <DeleteModal
                title={t("AssetManagement.bulkDeleteAssets.title")}
                isOpen={bulkDeleteModalVisible}
                hideModal={() => setBulkDeleteModalVisible(false)}
                onDelete={handleBulkDelete}
                textBundle={{
                    confirmationText: DELETE_CONFIRMATION_WORD,
                    deleteButtonText: t("AssetManagement.bulkDeleteAssets.delete"),
                    cancelButtonText: t("Common.cancel"),
                }}
                testIds={{
                    itself: testIds.common.confirmationDialog.itself,
                    cancelButtonId: testIds.common.dialog.closeButton,
                    deleteButtonId: testIds.common.confirmationDialog.confirmButton,
                }}
                dataType="BULK_DELETE_ASSETS"
            >
                <p>
                    {t("AssetManagement.bulkDeleteAssets.introductionMessage", {
                        count: selectedAssetIds.size,
                    })}
                </p>
            </DeleteModal>

            <Modal isOpen={isBulkDeleting} hideModal={() => setIsBulkDeleting(false)} isLoading>
                <></>
            </Modal>

            <AlertModal
                title={t(
                    bulkDeleteResult?.deletedCount == 0
                        ? "AssetManagement.bulkDeleteAssets.errorTitle"
                        : "AssetManagement.bulkDeleteAssets.partialFailureTitle"
                )}
                variant={bulkDeleteResult?.deletedCount == 0 ? "Error" : "Warning"}
                buttons={
                    <Button
                        variant={"Primary"}
                        testId={testIds.common.dialog.closeButton}
                        onClick={() => setIsBulkDeleteResultModalOpen(false)}
                    >
                        {t("Common.close")}
                    </Button>
                }
                isOpen={isBulkDeleteResultModalOpen}
                hideModal={() => setIsBulkDeleteResultModalOpen(false)}
            >
                <Box>
                    {bulkDeleteResult?.deletedCount == 0
                        ? t("AssetManagement.bulkDeleteAssets.failureMessage", "Common.failureMessage")
                        : t("AssetManagement.bulkDeleteAssets.partialFailureMessage", {
                              deletedCount: bulkDeleteResult?.deletedCount,
                              failedCount: bulkDeleteResult?.failedCount,
                          })}
                </Box>
            </AlertModal>

            <MoveAssetsToGroupModal
                isOpen={isMoveToGroupModalOpen}
                onClose={() => setIsMoveToGroupModalOpen(false)}
                onSuccess={() => setSelectedAssetIds(new Map())}
                selectedAssetIds={moveToGroupAssetIds}
                selectedAssetCount={moveToGroupAssetIds.length}
                currentViewUuid={props.viewId}
            />

            <TableFooter
                className={style.tableFooter}
                leftColumn={
                    <Box flexWrap="Wrap" className={style.tagsContainer}>
                        {createBottomFilterContainer(
                            DataBearingFilter.ALL,
                            false,
                            t("AssetManagement.dataBearingFilter.all"),
                            tableTrackingSummary !== undefined
                                ? tableTrackingSummary.trackedCount + tableTrackingSummary.nonDataBearingCount
                                : tableState.totalAssetCount
                        )}
                        {createBottomFilterContainer(
                            DataBearingFilter.DATA_BEARING,
                            true,
                            t("AssetManagement.dataBearingFilter.dataBearing"),
                            tableTrackingSummary?.trackedCount ?? 0
                        )}
                        {createBottomFilterContainer(
                            DataBearingFilter.NON_DATA_BEARING,
                            false,
                            t("AssetManagement.dataBearingFilter.nonDataBearing"),
                            tableTrackingSummary?.nonDataBearingCount ?? 0
                        )}
                    </Box>
                }
                rightColumn={
                    tableState.totalAssetCount > 0 && (
                        <Pagination
                            className={style.pagination}
                            maxPageNumbersToShow={MAX_PAGE_NUMBERS_TO_SHOW}
                            totalPages={tableState.totalPages}
                            currentPage={tableState.currentPage}
                            handlePageChange={(page: number) => goToPage(page)}
                            paginationText={t("AssetManagement.showingAssets", {
                                start: (tableState.currentPage - 1) * PAGE_SIZE + 1,
                                end: (tableState.currentPage - 1) * PAGE_SIZE + tableState.assetCount,
                                total: tableState.totalAssetCount,
                            })}
                        />
                    )
                }
            />
        </Box>
    );
};

export default AssetTable;
